import type {
  ObjectRow,
  ShiftWithCalc,
  Period,
  PeriodSummary,
  Holiday,
  DashboardData,
  UserProfile,
  ComplianceWarning,
  DocumentRow,
  CompareResult,
  AfwijkingType,
} from '../types';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

export interface CalendarSettings {
  sync_work: boolean;
  sync_bhv: boolean;
  sync_opleiding: boolean;
  sync_vakantie: boolean;
  sync_ziekte: boolean;
  sync_buitengewoon: boolean;
  reminder_minutes: number;
  include_bruto_in_title: boolean;
  title_amount: 'geen' | 'bruto' | 'netto';
}

/** Event-naam die de AuthProvider opvangt om netjes naar het login-scherm te gaan
 *  zodra de sessie verlopen blijkt (i.p.v. stille fouten overal). */
export const SESSION_EXPIRED_EVENT = 'trigion:session-expired';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (!resp.ok) {
    // Sessie verlopen → globaal signaleren zodat de app naar login redirect
    // (behalve voor /me zelf, dat checkt auth bewust en mag stil 'false' geven).
    if (resp.status === 401 && !path.startsWith('/api/auth/')) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      }
    }
    const body = await resp.text();
    throw new Error(`${resp.status} ${resp.statusText}: ${body}`);
  }
  if (resp.status === 204) return null as T;
  return resp.json() as Promise<T>;
}

export const api = {
  // Auth
  auth: {
    me: () => request<{ authenticated: boolean; email?: string; profile?: any }>('/api/auth/me'),
    logout: () => request('/api/auth/logout', { method: 'POST' }),
  },

  // Profile
  profile: {
    get: () => request<UserProfile>('/api/profile'),
    patch: (data: Partial<UserProfile>) =>
      request<{ ok: true }>('/api/profile', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
  },

  // Objects
  objects: {
    list: () => request<ObjectRow[]>('/api/objects'),
    create: (data: Partial<ObjectRow>) =>
      request<ObjectRow>('/api/objects', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: Partial<ObjectRow>) =>
      request<ObjectRow>(`/api/objects/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: number) =>
      request<{ ok: true }>(`/api/objects/${id}`, { method: 'DELETE' }),
  },

  // Shifts
  shifts: {
    list: (from: string, to: string) =>
      request<ShiftWithCalc[]>(`/api/shifts?from=${from}&to=${to}`),
    get: (id: number) => request<ShiftWithCalc>(`/api/shifts/${id}`),
    create: (data: {
      shift_date: string;
      start_time: string;
      end_time: string;
      object_id?: number | null;
      status?: string;
      label?: string | null;
      notes?: string | null;
      leave_type?: string;
    }) =>
      request<{ id: number; calc: any; warnings: ComplianceWarning[] }>('/api/shifts', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: number, data: any) =>
      request<{ id: number; calc: any }>(`/api/shifts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (id: number) => request<{ ok: true }>(`/api/shifts/${id}`, { method: 'DELETE' }),
    setAfwijking: (
      id: number,
      data: {
        type: 'ziek' | 'verlof' | 'geruild' | 'gewerkt';
        actual_start_time?: string | null;
        actual_end_time?: string | null;
        pauze_min?: number | null;
        object_id?: number | null;
        verlof_soort?: 'vakantie' | 'buitengewoon' | 'bijzonder_verlof';
        notes?: string | null;
      }
    ) =>
      request<{ ok: true; total_gross: number; total_hours: number }>(`/api/shifts/${id}/afwijking`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    clearAfwijking: (id: number) =>
      request<{ ok: true; total_gross: number; total_hours: number }>(`/api/shifts/${id}/afwijking`, {
        method: 'DELETE',
      }),
    periods: (from: string, to: string) => request<CalendarPeriod[]>(`/api/shifts/periods?from=${from}&to=${to}`),
    periodSummary: (year: number, period: number) =>
      request<PeriodSummary>(`/api/shifts/period-summary/${year}/${period}`),
    copyRange: (data: { source_from: string; source_to: string; target_from: string }) =>
      request<{ ok: true; created: number }>('/api/shifts/copy-range', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  // Expenses (onkostendeclaraties)
  expenses: {
    list: () => request<any[]>('/api/expenses'),
    create: (data: any) =>
      request<{ id: number }>('/api/expenses', { method: 'POST', body: JSON.stringify(data) }),
    delete: (id: number) => request<{ ok: true }>(`/api/expenses/${id}`, { method: 'DELETE' }),
    uploadReceipt: async (id: number, file: File) => {
      const buf = await file.arrayBuffer();
      const resp = await fetch(`${BASE}/api/expenses/${id}/receipt`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          'X-Original-Filename': encodeURIComponent(file.name),
        },
        body: buf,
      });
      if (!resp.ok) throw new Error(`${resp.status}: ${await resp.text()}`);
      return resp.json() as Promise<{ ok: boolean; r2_key: string }>;
    },
  },

  // Periodieke events
  events: {
    upcoming: (days?: number) =>
      request<{ events: Array<{ id: number; kind: string; title: string; description: string | null; next_date: string; days_until: number }> }>(
        `/api/events/upcoming${days ? `?days=${days}` : ''}`
      ),
    create: (data: { kind: string; title: string; description?: string; first_trigger_date: string; recurrence?: string }) =>
      request<{ id: number }>('/api/events', { method: 'POST', body: JSON.stringify(data) }),
    delete: (id: number) => request<{ ok: true }>(`/api/events/${id}`, { method: 'DELETE' }),
  },

  // CAO
  cao: {
    rates: (date?: string) => request<any>(`/api/cao/rates${date ? `?date=${date}` : ''}`),
    holidays: (year: number) => request<Holiday[]>(`/api/cao/holidays?year=${year}`),
    periods: (year: number) => request<Period[]>(`/api/cao/periods?year=${year}`),
    wages: (date?: string) =>
      request<{ cao_version: string; wages: Array<{ schaal: number; trede: number; uurloon: number }> }>(
        `/api/cao/wages${date ? `?date=${date}` : ''}`
      ),
    checkCompliance: (data: { shift_date: string; start_time: string; end_time: string; exclude_shift_id?: number }) =>
      request<{ warnings: ComplianceWarning[] }>('/api/cao/check-compliance', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  // Salary — trede-verhoging tracker
  salary: {
    nextTredeBump: () =>
      request<{
        current: { functie: string; schaal: number; trede: number; uurloon: number; effective_since: string };
        next: { trede: number; expected_uurloon: number | null; delta_eur: number | null; delta_pct: number | null };
        next_bump_date: string;
        days_until: number;
        at_max_trede: boolean;
        max_trede: number | null;
        source_cao_version: string | null;
        hire_date: string;
      }>('/api/salary/next-trede-bump'),
  },

  // Onboarding
  onboarding: {
    progress: () =>
      request<{
        current_step: number;
        completed_steps: number[];
        is_completed: boolean;
        progress_data: Record<string, any> | null;
      }>('/api/onboarding/progress'),
    updateProgress: (data: {
      current_step?: number;
      completed_steps?: number[];
      progress_data?: Record<string, any>;
    }) => request('/api/onboarding/progress', { method: 'POST', body: JSON.stringify(data) }),
    complete: () => request('/api/onboarding/complete', { method: 'POST' }),
    reset: () => request('/api/onboarding/reset', { method: 'POST' }),
  },

  // Dashboard
  dashboard: {
    get: () => request<DashboardData>('/api/dashboard'),
    /** Wat de periode oplevert bij een ander uurloon. */
    simulatie: (uurlonen: number[], year?: number, period?: number) =>
      request<UurloonSimulatie>(
        `/api/dashboard/simulatie?uurloon=${uurlonen.join(',')}${year && period ? `&year=${year}&period=${period}` : ''}`
      ),
    /** Eigen proefberekening met de echte diensten van de periode. */
    proef: (year?: number, period?: number) =>
      request<EigenProef>(`/api/dashboard/proefberekening${year && period ? `?year=${year}&period=${period}` : ''}`),
  },

  // Regels en cijfers uit de documenten
  regels: () => request<RegelsOverzicht>('/api/regels'),

  // Documents
  documents: {
    list: (category?: string, year?: number) => {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (year) params.set('year', String(year));
      return request<DocumentRow[]>(`/api/documents?${params}`);
    },
    get: (id: number) =>
      request<{
        id: number;
        category: string;
        subcategory: string | null;
        renamed_filename: string | null;
        original_filename: string | null;
        year: number | null;
        period_number: number | null;
        document_date: string | null;
        parse_status: string;
        parse_confidence: string | null;
        parsed_json: any | null;
        parse_notes: string | null;
        r2_key: string;
        file_size: number | null;
        mime_type: string | null;
      }>(`/api/documents/${id}`),
    fileUrl: (id: number) => `${BASE}/api/documents/${id}/file`,
    patch: (id: number, data: { parsed_json?: any; parse_confidence?: string; parse_note?: string }) =>
      request<{ ok: true }>(`/api/documents/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (id: number) =>
      request<{ ok: true }>(`/api/documents/${id}`, { method: 'DELETE' }),
    stats: (year?: number) =>
      request<{
        total: number;
        low_confidence: number;
        by_category: Record<string, number>;
        years: Array<{ year: number; n: number }>;
      }>(`/api/documents/stats${year ? `?year=${year}` : ''}`),
    search: (q: string) =>
      request<DocumentRow[]>(`/api/documents/search?q=${encodeURIComponent(q)}`),
    byPeriod: (year?: number) =>
      request<Array<{
        year: number;
        period_number: number;
        start_date?: string;
        end_date?: string;
        payday?: string;
        any_low_confidence: boolean;
        loonstrook: DocumentRow | null;
        urenspec: DocumentRow | null;
        urencontrole: DocumentRow | null;
        myrostar: DocumentRow[];
        other: DocumentRow[];
      }>>(`/api/documents/by-period${year ? `?year=${year}` : ''}`),
    yearZipUrl: (year: number) => `${BASE}/api/documents/export/${year}/zip`,
    mobileUpload: async (file: File, category: string) => {
      const buf = await file.arrayBuffer();
      const resp = await fetch(`${BASE}/api/documents/mobile-upload`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': file.type || 'application/pdf',
          'X-Original-Filename': encodeURIComponent(file.name),
          'X-Document-Category': category,
        },
        body: buf,
      });
      if (!resp.ok) throw new Error(`${resp.status}: ${await resp.text()}`);
      return resp.json() as Promise<{ document_id: number; status: string; categorie?: string; titel?: string }>;
    },
  },

  // Compare
  compare: {
    get: (year: number, period: number) =>
      request<CompareResult>(`/api/compare/${year}/${period}`),
    email: (year: number, period: number) =>
      request<{ email_text: string; to: string }>(`/api/compare/${year}/${period}/email`),
  },

  // RostarCAS sync
  acties: {
    status: () => request<ActiesStatus>('/api/acties/status'),
    kosten: () =>
      request<{ maand: string; totaal: number; gemeten_sinds: string | null; per_onderdeel: Array<{ onderdeel: string; aanroepen: number; kosten: number }> }>(
        '/api/acties/kosten'
      ),
    upload: (kind: 'loonstrook' | 'urenspec' | 'urencontrole' | 'rooster', file: File) =>
      request<UploadResult>(`/api/acties/upload?kind=${kind}`, {
        method: 'POST',
        body: file,
        headers: { 'Content-Type': 'application/pdf', 'X-Filename': file.name },
      }),
    verlofAanvullen: () =>
      request<{ created: number; converted: number; exists: number; hours: number }>('/api/leave/backfill-from-urenspecs', {
        method: 'POST',
      }),
    recalcBatch: (afterId: number, limit = 6) =>
      request<{ report: Array<{ shift_id: number }>; remaining?: number; shifts_with_error?: number }>('/api/cao/recalc-all-segments', {
        method: 'POST',
        body: JSON.stringify({ after_shift_id: afterId, limit }),
      }),
  },

  vragen: {
    lijst: () => request<{ gesprekken: Array<{ id: number; titel: string; updated_at: string }> }>('/api/vragen'),
    gesprek: (id: number) => request<VragenGesprek>(`/api/vragen/${id}`),
    stel: (vraag: string, gesprekId?: number) =>
      request<{ gesprek_id: number }>('/api/vragen', { method: 'POST', body: JSON.stringify({ vraag, gesprek_id: gesprekId }) }),
    verder: (id: number) =>
      request<{ klaar: boolean; wat: string; antwoord?: string; bronnen?: string[] }>(`/api/vragen/${id}/verder`, { method: 'POST' }),
    verwijder: (id: number) => request<{ ok: boolean }>(`/api/vragen/${id}`, { method: 'DELETE' }),
  },

  automatisch: {
    list: () => request<AutomatischData>('/api/automatisch'),
    update: (taak: AutomatischTaak, v: { aan?: boolean; elke_uren?: number }) =>
      request<AutomatischData>(`/api/automatisch/${taak}`, { method: 'PATCH', body: JSON.stringify(v) }),
  },

  gmail: {
    status: () => request<GmailStatus>('/api/gmail/status'),
    sync: () =>
      request<{ status: string; processed: Array<{ subject: string; kind: string; summary: string | null }>; remaining: number; error?: string }>(
        '/api/gmail/sync',
        { method: 'POST' }
      ),
    archief: () => request<GmailArchiefResult>('/api/gmail/archief', { method: 'POST' }),
  },

  netto: {
    factoren: (from: string, to: string) =>
      request<{ factoren: NettoFactor[] }>(`/api/netto/factoren?from=${from}&to=${to}`),
  },
  afwijkingen: {
    get: () => request<AfwijkingenData>('/api/afwijkingen'),
    /** Recente urencontroles, per dag naast de diensten in de app. */
    urencontroles: () => request<UrencontroleData>('/api/afwijkingen/urencontroles'),
    /** Rooster bijwerken: feed=true bewaart eerst de feed van nu; elke aanroep vult daarna één voorbije week aan. */
    urencontrolesRooster: (feed: boolean) =>
      request<{ feed_fout: string | null; gedaan: number; over: number }>(`/api/afwijkingen/urencontroles/rooster${feed ? '?feed=1' : ''}`, { method: 'POST' }),
    /** Nieuwe Trigion-mail ophalen en de urencontrole opnieuw vergelijken met de app. */
    urencontrolesCheck: () => request<UrencontroleCheck>('/api/afwijkingen/urencontroles/check', { method: 'POST' }),
    /** Mailtekst voor de planner over één periode. Verstuurt niets. */
    mail: (year: number, period: number) =>
      request<{ subject: string; body: string; meldingen: number; dagen: number; leeg: boolean }>(
        `/api/afwijkingen/mail?year=${year}&period=${period}`
      ),
  },

  uitkeringen: {
    get: () => request<UitkeringenData>('/api/uitkeringen'),
    dagen: (from: string, to: string) => request<BelangrijkeDag[]>(`/api/uitkeringen/dagen?from=${from}&to=${to}`),
  },

  verschuivingen: {
    forShift: (shiftId: number) => request<{ items: Verschuiving[] }>(`/api/verschuivingen?shift_id=${shiftId}`),
    list: (from: string, to: string) => request<{ items: Verschuiving[] }>(`/api/verschuivingen?from=${from}&to=${to}`),
    setStatus: (id: number, status: 'bevestigd' | 'afgewezen' | 'voorgesteld') =>
      request<{ ok: true }>(`/api/verschuivingen/${id}`, { method: 'POST', body: JSON.stringify({ status }) }),
  },

  afas: {
    status: () => request<AfasStatus>('/api/afas/status'),
    sync: () => request<AfasSyncResult>('/api/afas/sync', { method: 'POST' }),
    submit2fa: (code: string) =>
      request<AfasSyncResult>('/api/afas/2fa', { method: 'POST', body: JSON.stringify({ code }) }),
    /** De laatst opgehaalde AFAS-tabbladen (taken, dossier, verzuim, …). */
    portal: () => request<AfasPortal>('/api/afas/portal'),
    /** Ververs de 2 oudste AFAS-tabbladen (salaris, contracten, …); herhalen tot remaining 0. */
    portalSync: () =>
      request<{ fetched: string[]; remaining: number; changes: string[]; status?: string; error?: string }>('/api/afas/portal', { method: 'POST' }),
  },

  rooster: {
    status: () =>
      request<{
        credentials: { captured_at: string; expires_at: string | null; last_used_at: string | null; last_status: string | null } | null;
        last_sync: { synced_at: string; shifts_created: number; shifts_seen: number; source: string } | null;
      }>('/api/rooster/status'),
    sync: (opts?: { start_date?: string; days_ahead?: number }) =>
      request<{
        ok: boolean;
        shifts_seen?: number;
        shifts_created?: number;
        shifts_duplicate?: number;
        shifts_updated?: number;
        error?: string;
        body_snippet?: string;
      }>('/api/rooster/sync', { method: 'POST', body: JSON.stringify(opts ?? {}) }),
    dedupe: () =>
      request<{ ok: true; groups_merged: number; shifts_removed: number; details: any[] }>(
        '/api/rooster/dedupe',
        { method: 'POST' }
      ),
    seedObjects: () =>
      request<{ ok: true; shifts_linked: number; total_objects: number }>(
        '/api/rooster/seed-objects',
        { method: 'POST' }
      ),
    setCookies: (data: {
      cookies: Array<{
        name: string;
        value: string;
        domain?: string;
        path?: string;
        expires?: number;
        httpOnly?: boolean;
        secure?: boolean;
        sameSite?: string;
      }>;
      user_agent?: string;
      expires_at?: string;
    }) =>
      request<{ ok: true; id: number; cookies_stored: number }>('/api/rooster/set-cookies', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    // ICS-feed (primaire bron; geen cookies nodig)
    icsStatus: () =>
      request<{
        configured: boolean;
        ics_url_masked: string | null;
        last_fetched_at: string | null;
        last_status: string | null;
        last_event_count: number | null;
        last_sync: { synced_at: string; shifts_created: number; shifts_seen: number; source: string } | null;
      }>('/api/rooster/ics-status'),
    setIcsUrl: (ics_url: string) =>
      request<{ ok: true; id: number }>('/api/rooster/ics-url', {
        method: 'POST',
        body: JSON.stringify({ ics_url }),
      }),
    icsSync: () =>
      request<{
        ok: boolean;
        status: string;
        events_total?: number;
        shifts_seen?: number;
        created?: number;
        updated?: number;
        unchanged?: number;
        skipped?: number;
        range?: { start: string; end: string };
        error?: string;
      }>('/api/rooster/ics-sync', { method: 'POST' }),
  },

  // Google Calendar
  calendar: {
    status: () =>
      request<{
        connected: boolean;
        connected_at?: string;
        calendar_id?: string | null;
        calendar_name?: string;
        last_sync_at?: string | null;
        last_sync_status?: string | null;
        events_mapped?: number;
        queue_pending?: number;
      }>('/api/calendar/status'),
    connectUrl: () => `${BASE}/api/calendar/connect`,
    disconnect: () => request<{ ok: true }>('/api/calendar/disconnect', { method: 'POST' }),
    pushAll: (data?: { from?: string; to?: string; force?: boolean; inline?: boolean }) =>
      request<{ ok: boolean; created: number; updated: number; skipped: number; duplicates_removed?: number; orphans_removed?: number; failed: Array<{ id: number; error: string }> }>(
        '/api/calendar/push-all',
        { method: 'POST', body: JSON.stringify(data ?? {}) }
      ),
    reconcile: (data?: { from?: string; to?: string }) =>
      request<{
        ok: boolean;
        scanned: number;
        duplicates_removed: number;
        orphans_removed: number;
        created: number;
        updated: number;
        enqueued: number;
        more: boolean;
        failed: Array<{ id: number | string; error: string }>;
        error?: string;
        message?: string;
      }>('/api/calendar/reconcile', { method: 'POST', body: JSON.stringify(data ?? {}) }),
    settings: () =>
      request<CalendarSettings>('/api/calendar/settings'),
    updateSettings: (data: Partial<CalendarSettings>) =>
      request<CalendarSettings>('/api/calendar/settings', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    queue: () =>
      request<{
        counts: { pending: number; dead: number; done: number };
        pending: Array<{
          id: number;
          shift_id: number;
          action: string;
          attempts: number;
          last_error: string | null;
          enqueued_at: string;
          next_attempt_at: string | null;
        }>;
        recent: Array<{
          id: number;
          shift_id: number;
          action: string;
          attempts: number;
          last_error: string | null;
          processed_at: string;
        }>;
      }>('/api/calendar-queue'),
    runQueue: () =>
      request<{ ok: true; processed: number; succeeded: number; retried: number; failed: number; dead: number }>(
        '/api/calendar-queue/run',
        { method: 'POST' }
      ),
    clearDead: () =>
      request<{ ok: true; deleted: number }>('/api/calendar-queue/clear-dead', {
        method: 'POST',
      }),
  },

  // Push notifications (VAPID)
  push: {
    vapidPublicKey: () => request<{ publicKey: string }>('/api/push/vapid-public-key'),
    subscribe: (sub: PushSubscriptionJSON) =>
      request<{ ok: true }>('/api/push/subscribe', { method: 'POST', body: JSON.stringify(sub) }),
    unsubscribe: (endpoint: string) =>
      request<{ ok: true }>('/api/push/subscribe', { method: 'DELETE', body: JSON.stringify({ endpoint }) }),
    test: () => request<{ ok: boolean; sent: number }>('/api/push/test', { method: 'POST' }),
  },

  // Stats
  stats: {
    summary: (from: string, to: string) =>
      request<{
        periods: Array<{ year: number; period: number; bruto: number; uren: number; reiskosten: number; start_date: string }>;
        rate_totals: Record<string, { hours: number; bruto: number }>;
        objects: Array<{ object_naam: string; hours: number; shifts: number }>;
        cumulative: Array<{ date: string; bruto_cum: number }>;
      }>(`/api/stats/summary?from=${from}&to=${to}`),
    loon: (year: number) => request<StatsLoonResponse>(`/api/stats/loon/${year}`),
    uren: (year: number) => request<StatsUrenResponse>(`/api/stats/uren/${year}`),
    objecten: (year: number) => request<StatsObjectenResponse>(`/api/stats/objecten/${year}`),
    verdiensten: (year: number) => request<StatsVerdienstenResponse>(`/api/stats/verdiensten/${year}`),
    verlof: (year: number) => request<StatsVerlofResponse>(`/api/stats/verlof/${year}`),
    trends: (years?: number[]) =>
      request<StatsTrendsResponse>(
        `/api/stats/trends${years && years.length ? `?years=${years.join(',')}` : ''}`
      ),
  },

  // Tax / exports
  tax: {
    yearUrl: (year: number) => `${BASE}/api/export/belastingaangifte/${year}`,
  },
  exportView: {
    jaaroverzichtXlsxUrl: (year: number) => `${BASE}/api/export/jaaroverzicht/${year}.xlsx`,
    statsTabXlsxUrl: (tab: StatsTab, year: number) => `${BASE}/api/export/stats/${tab}/${year}.xlsx`,
  },

  /* CLAIM-D */
  leave: {
    balance: () =>
      request<{
        snapshot_date: string | null;
        year: number | null;
        period_number: number | null;
        wettelijk_saldo: number;
        wettelijk_geldig_tot: string | null;
        bovenwettelijk_saldo: number;
        bovenwettelijk_geldig_tot: string | null;
        feestdagverlof_saldo: number;
        source: 'snapshot' | 'empty';
      }>('/api/leave/balance'),
    history: (year?: number) =>
      request<{
        snapshots: Array<{
          snapshot_date: string;
          year: number;
          period_number: number;
          wettelijk_opbouw: number | null;
          wettelijk_saldo: number | null;
          bovenwettelijk_opbouw: number | null;
          bovenwettelijk_saldo: number | null;
          feestdagverlof_saldo: number | null;
        }>;
      }>(`/api/leave/history${year ? `?year=${year}` : ''}`),
    opbouw: (urenPerWeek: number) =>
      request<{ wettelijk_opbouw: number; bovenwettelijk_opbouw: number; uren_per_week: number }>(
        `/api/leave/opbouw-per-periode?uren_per_week=${urenPerWeek}`
      ),
    ziekteLoon: (data: { shift_date: string; hours: number }) =>
      request<{
        dag_nummer: number;
        weeks_in_periode: number;
        percentage: number;
        basisloon_ziek: number;
        ort_aanvulling: number;
        total: number;
        notes: string[];
      }>('/api/leave/ziekte-loon', { method: 'POST', body: JSON.stringify(data) }),
    vakantieOrt: (data: { shift_date: string; hours: number }) =>
      request<{ per_uur: number; total: number }>('/api/leave/vakantie-ort-aanvulling', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    importFromUrenspecs: () =>
      request<{ ok: true; snapshots_written: number }>('/api/leave/import-from-urenspecs', {
        method: 'POST',
      }),
    ziektePeriodes: {
      list: () =>
        request<Array<{ id: number; start_date: string; end_date: string | null; reason: string | null; hersteld_gemeld: number; notes: string | null }>>(
          '/api/leave/ziekte-periodes'
        ),
      create: (data: { start_date: string; end_date?: string | null; reason?: string | null; notes?: string | null }) =>
        request<{ id: number }>('/api/leave/ziekte-periodes', { method: 'POST', body: JSON.stringify(data) }),
      delete: (id: number) => request<{ ok: true }>(`/api/leave/ziekte-periodes/${id}`, { method: 'DELETE' }),
    },
  },

  feestdagen: {
    list: (year: number) =>
      request<{
        year: number;
        holidays: Array<{
          id: number;
          holiday_date: string;
          name: string;
          is_trigion_recognized: number;
          hours_worked: number;
          shift_id: number | null;
          opbouw_registered: number | null;
          registration_exists: boolean;
        }>;
      }>(`/api/feestdagen?year=${year}`),
    berekenLoon: (data: {
      shift_date: string;
      start_time: string;
      end_time: string;
      gewerkt?: boolean;
      gewoonlijk_uren?: number;
    }) =>
      request<{
        is_feestdag: boolean;
        feestdag_naam: string | null;
        gewerkt: boolean;
        feestdag_toeslag: number;
        basisloon: number;
        feestdagverlof_opbouw: number;
        doorbetaling_gemist: number;
        total_bruto: number;
        notes: string[];
      }>('/api/feestdagen/bereken-loon', { method: 'POST', body: JSON.stringify(data) }),
    register: (data: { holiday_date: string; shift_id?: number | null; worked: boolean; notes?: string | null }) =>
      request<{ id: number; updated: boolean; opbouw_uren: number }>('/api/feestdagen/register', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    saldo: () =>
      request<{ total_opgebouwd_uren: number; gewerkte_feestdagen: number }>('/api/feestdagen/saldo'),
  },

  /* CLAIM-K */
  notifications: {
    list: (
      status: 'unread' | 'read' | 'dismissed' | 'all' = 'unread',
      opts?: { kind?: string; limit?: number; offset?: number }
    ) => {
      const params = new URLSearchParams({ status });
      if (opts?.kind) params.set('kind', opts.kind);
      if (opts?.limit) params.set('limit', String(opts.limit));
      if (opts?.offset) params.set('offset', String(opts.offset));
      return request<{
        notifications: Array<{
          id: number;
          kind: string | null;
          type: string;
          priority: 'low' | 'medium' | 'high';
          title: string;
          body: string | null;
          link: string | null;
          related_kind: string | null;
          related_id: number | null;
          expiry_date: string | null;
          payload_json: string | null;
          read_at: string | null;
          seen_at: string | null;
          dismissed_at: string | null;
          action_taken: string | null;
          created_at: string;
        }>;
        unread_count: number;
        total: number;
      }>(`/api/notifications?${params}`);
    },
    summary: () => request<{ unread: number; high: number }>('/api/notifications/summary'),
    markSeen: () => request<{ ok: true }>('/api/notifications/seen', { method: 'POST' }),
    markRead: (id: number) =>
      request<{ ok: true }>(`/api/notifications/${id}/read`, { method: 'POST' }),
    markUnread: (id: number) =>
      request<{ ok: true }>(`/api/notifications/${id}/unread`, { method: 'POST' }),
    dismiss: (id: number) =>
      request<{ ok: true }>(`/api/notifications/${id}/dismiss`, { method: 'POST' }),
    readAll: () =>
      request<{ ok: true }>('/api/notifications/read-all', { method: 'POST' }),
    action: (id: number, action: string) =>
      request<{ ok: true }>(`/api/notifications/${id}/action`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      }),
    prefs: () =>
      request<{ prefs: Array<{ kind: string; label: string; enabled: boolean }> }>(
        '/api/notifications/prefs'
      ),
    setPref: (kind: string, enabled: boolean) =>
      request<{ ok: true }>('/api/notifications/prefs', {
        method: 'POST',
        body: JSON.stringify({ kind, enabled }),
      }),
    runProducers: () =>
      request<{
        total_created: number;
        per_kind: Array<{ kind: string; created: number; skipped_muted: boolean }>;
        errors: Array<{ kind: string; message: string }>;
      }>('/api/notifications/run-producers', { method: 'POST' }),
  },
};

// =====================================================================
// Stats response types
// =====================================================================
export type StatsTab = 'loon' | 'uren' | 'objecten' | 'verdiensten' | 'verlof' | 'trends';

export interface VerdienstenRate {
  rate_type: string;
  label: string;
  hours: number;
  multiplier: number;
  pct: number;
  base_hourly_wage: number;
  effective_hourly_wage: number;
  bruto: number;
}

export interface VerdienstenObject {
  object_id: number | null;
  object_naam: string;
  distance_km: number | null;
  shifts: number;
  hours: number;
  bruto: number;
  effective_hourly_wage: number;
  rate_breakdown: VerdienstenRate[];
}

export interface StatsVerdienstenResponse {
  year: number;
  totals: { hours: number; bruto: number; effective_hourly_wage: number };
  rate_breakdown: VerdienstenRate[];
  objects: VerdienstenObject[];
}

export interface Kpi {
  value: number;
  prev: number;
  delta: number;
}

export interface StatsLoonPeriod {
  period: number;
  start_date: string | null;
  bruto: number;
  netto: number;
  reiskosten: number;
  loonheffing: number;
  premie: number;
  uren: number;
  gem_per_uur: number;
  inhoudingen: { pensioen: number; loonheffing: number; premie_ww: number; fba: number; overig: number };
}

export interface StatsLoonResponse {
  year: number;
  kpis: { bruto: Kpi; netto: Kpi; reiskosten: Kpi; gem_per_uur: Kpi };
  periods: StatsLoonPeriod[];
  cumulative: Array<{ period: number; curr_cum: number | null; prev_cum: number | null }>;
  totals: { bruto: number; netto: number; reiskosten: number; loonheffing: number; uren: number };
}

export interface StatsUrenResponse {
  year: number;
  kpis: { gewerkt: Kpi; verlof: Kpi; ziekte: Kpi; bruto: Kpi };
  periods: Array<{ period: number; start_date: string; gewerkt: number; verlof: number; ziekte: number }>;
  rate_totals: Array<{ rate_type: string; hours: number; bruto: number }>;
  day_of_week: Array<{ day: string; hours: number }>;
  contract_uren_per_periode: number;
}

export interface StatsObjectenResponse {
  year: number;
  kpis: { unieke_objecten: Kpi; top_object_pct: Kpi; totaal_km: Kpi; nacht_uren: Kpi };
  objects: Array<{
    object_id: number | null; object_naam: string; hours: number; shifts: number;
    distance_km: number | null; km_total: number; bruto: number;
    nacht_hours: number; weekend_hours: number;
  }>;
}

export interface StatsVerlofResponse {
  year: number;
  kpis: { wettelijk_saldo: Kpi; bovenwettelijk_saldo: Kpi; feestdagverlof: Kpi; verbruikt_ytd: Kpi };
  snapshots: Array<{
    period: number; date: string;
    wettelijk_opbouw: number; wettelijk_saldo: number;
    bovenwettelijk_opbouw: number; bovenwettelijk_saldo: number;
    feestdagverlof_saldo: number;
  }>;
  consumed: Array<{ start_date: string; end_date: string; hours: number; leave_type: string }>;
  planned: Array<{ start_date: string; end_date: string; hours: number; leave_type: string; status: string }>;
}

export interface StatsTrendsResponse {
  years: number[];
  kpis: { bruto_yoy: Kpi; uren_yoy: Kpi; ort_pct_yoy: Kpi; gem_uurloon_yoy: Kpi };
  by_year: Array<{
    year: number; bruto: number; netto: number; uren: number; reiskosten: number;
    ort_amount: number; ort_pct: number; periods_count: number;
  }>;
  period_comparison: Array<{ year: number; period_number: number; bruto: number; uren: number }>;
}

export interface AfasSyncResult {
  status: 'ok' | 'needs_2fa' | 'error' | 'not_configured' | 'skipped';
  imported: Array<{ kind: string; year: number; period: number }>;
  remaining: number;
  error?: string;
  portal?: { fetched: string[]; changes: string[] };
}

export interface AfasStatus {
  configured: boolean;
  status: 'never' | 'ok' | 'needs_2fa' | 'error' | 'not_configured';
  trusted_until: string | null;
  last_sync_at: string | null;
  last_error: string | null;
  documents: Array<{ kind: string; year: number; period_number: number; status: string; error: string | null; published_at: string | null }>;
}

export interface PeriodeVerschil {
  year: number;
  period: number;
  start_date: string;
  end_date: string;
  payday: string | null;
  loonstrook_bruto: number | null;
  app_bruto: number;
  bijzonder: Array<{ omschrijving: string; bedrag: number }>;
  verschil: number | null;
  status: 'ok' | 'afwijking' | 'geen_loonstrook';
  dagen: Array<{ date: string; app_uren: number; trigion_uren: number; verschil: number; app: string | null; trigion: string | null }>;
}

export interface Uitkering {
  soort: 'eindejaarsuitkering' | 'vakantiegeld';
  year: number;
  bedrag: number | null;
  betaaldag: string | null;
  bron: 'loonstrook' | 'afas' | 'schatting';
  grondslag: number | null;
  geschatte_periodes: number;
  toelichting: string;
  netto?: number | null;
}

export interface UurloonScenario {
  uurloon: number;
  bruto: number;
  netto: number | null;
  op_rekening: number | null;
  verschil_bruto: number;
  verschil_netto: number | null;
  posten: Array<{ key: string; label: string; bruto: number; netto: number | null }>;
  jaar: { bruto: number; netto: number | null };
}

export interface UurloonSimulatie {
  year: number;
  period: number;
  start_date: string;
  end_date: string;
  uren: number;
  huidig: UurloonScenario;
  scenarios: UurloonScenario[];
}

export interface EigenProef {
  year: number;
  period: number;
  start_date: string;
  end_date: string;
  payday: string;
  regels: Array<{ omschrijving: string; aantal: string | null; bedrag: number; bijzonder?: boolean }>;
  bruto: number;
  inhoudingen: Array<{ omschrijving: string; bedrag: number }>;
  netto: number;
  reiskosten: number;
  uit_te_betalen: number;
  /** Zelfde posten en totaal als het overzicht (centrale berekening). */
  posten: Array<{ key: string; label: string; bruto: number; netto: number | null; uren?: number }>;
  totaal: { bruto: number; netto: number | null };
}

export interface NettoFactor {
  year: number;
  period_number: number;
  start_date: string;
  end_date: string;
  factor: number | null;
}

export interface UitkeringenData {
  eindejaarsuitkering: Uitkering;
  vakantiegeld: Uitkering;
  vorige: Uitkering[];
  proefberekening: {
    year: number | null;
    period: number | null;
    bruto: number | null;
    netto: number | null;
    uit_te_betalen: number | null;
    eindejaarsuitkering: number | null;
    regels: Array<{ omschrijving: string; aantal: string | null; bedrag: number | null }>;
    fetched_at: string;
  } | null;
}

export interface BelangrijkeDag {
  date: string;
  kind: 'eindejaarsuitkering' | 'vakantiegeld' | 'loonsverhoging' | 'kwalificatie' | 'verlof' | 'contract' | 'cao' | 'ziek';
  title: string;
  detail?: string;
  bedrag?: number | null;
  verwacht?: boolean;
}

export interface Verschuiving {
  id: number;
  shift_id: number;
  shift_date: string;
  notice_days: number;
  pct: number;
  uren: number;
  old_start: string | null;
  old_end: string | null;
  new_start: string;
  new_end: string;
  status: 'voorgesteld' | 'bevestigd' | 'afgewezen';
  bedrag: number;
}

export interface AfwijkingenData {
  eigen: Array<{
    id: number; shift_date: string; start_time: string; end_time: string;
    deviation_reason: AfwijkingType | null; leave_type: string | null; deviation_notes: string | null;
    object_naam_snapshot: string | null; bruto: number;
    actual_start_time: string | null; actual_end_time: string | null; actual_pauze_min: number | null;
    planned_object_naam: string | null;
  }>;
  periodes: PeriodeVerschil[];
  meldingen: Array<{ id: number; kind: string; priority: string; title: string; body: string | null; link: string | null; created_at: string; read_at: string | null }>;
  counts: { eigen: number; verschillen: number; meldingen: number };
}

export type UrencontroleStatus =
  | 'klopt'
  | 'urencontrole_wijkt_af'
  | 'app_wijkt_af'
  | 'rooster_wijkt_af'
  | 'alle_verschillen'
  | 'app_urencontrole_verschillen'
  | 'rooster_app_verschillen';

export interface UrencontroleDag {
  date: string;
  /** null: de rooster-feed bevat deze dag niet (meer). */
  rooster: number | null;
  app: number;
  /** Uren volgens de derde bron (urencontrole of urenbijlage); null als die er niet is. */
  urencontrole: number | null;
  rooster_regels: string[];
  app_regels: string[];
  urencontrole_regels: string[];
  /** Ziek volgens AFAS-verzuim, anders null. */
  ziek: string | null;
  /** Wat andere bronnen van die dag zeggen (eigen afwijking, urenbijlage, feestdag). */
  verklaring: string[];
  status: UrencontroleStatus;
}

export interface UrencontroleData {
  urencontroles: Array<{
    year: number;
    period: number;
    start_date: string;
    end_date: string;
    ingelezen_op: string | null;
    urencontrole_totaal: number | null;
    /** Welke bron naast rooster en app staat: urencontrole, anders urenbijlage (AFAS), anders geen. */
    derde: 'urencontrole' | 'urenbijlage' | null;
    urencontrole_uren: number | null;
    app_uren: number;
    loonstrook: { uren: number; bruto: number } | null;
    urenbijlage_uren: number | null;
    /** Loonstrook min urenbijlage in uren (alleen als het meer dan 1 uur scheelt). */
    loon_verschil: number | null;
    dagen: UrencontroleDag[];
  }>;
  /** Aantal dagen waar rooster, app en urencontrole niet overeenkomen. */
  open: number;
  /** Voorbije weken waarvan het rooster nog niet is opgehaald. */
  rooster_te_halen: number;
}

export interface UrencontroleCheck {
  nieuwe_urencontroles: number;
  mail_overgeslagen: string | null;
  nog_te_verwerken: number;
}

export interface ActiesStatus {
  afas: { status: string; last_sync_at: string | null; trusted_until: string | null; last_error: string | null } | null;
  gmail: { n: number; last: string | null; findings: number } | null;
  archief: { bekeken: number; totaal: number | null; klaar: number; nieuw: number; last: string | null } | null;
  rooster: { ics_last_fetched_at: string | null; ics_last_status: string | null } | null;
  agenda: { last_sync_at: string | null; last_sync_status: string | null; wachtrij: number };
  counts: { shifts: number; documents: number; loonstroken: number; urenspecs: number } | null;
  ontbrekend: Array<{ year: number; period: number; loonstrook: boolean; urenbijlage: boolean }>;
}

export interface VragenGesprek {
  gesprek: { id: number; titel: string; updated_at: string };
  bezig: boolean;
  berichten: Array<{ id: number; rol: 'user' | 'assistant'; tekst: string; bronnen: string[] }>;
}

export type AutomatischTaak = 'rooster' | 'gmail' | 'agenda' | 'archief' | 'afas' | 'onderhoud';

export interface AutomatischData {
  taken: Array<{ taak: AutomatischTaak; aan: boolean; elke_uren: number; laatst_gedraaid: string | null; volgende: string | null }>;
  intervallen: Record<AutomatischTaak, number[]>;
}

export interface GmailArchiefResult {
  status: 'ok' | 'error';
  nieuw: Array<{ titel: string; categorie: string }>;
  dubbel: number;
  fouten: number;
  mails: number;
  klaar: boolean;
  totaal: number | null;
  bekeken: number;
  error?: string;
}

export interface UploadResult {
  ok: boolean;
  kind: string;
  message: string;
  year?: number;
  period?: number;
  duplicate?: boolean;
  van?: string;
  tot?: string;
  diffs?: Array<{ date: string; type: string; trigion: string | null; app: string | null }>;
}

export interface GmailStatus {
  connected: boolean;
  counts: { total: number; findings: number } | null;
  recent: Array<{ id: string; received_at: string; from_addr: string; subject: string; kind: string; status: string; summary: string | null; findings: number; error: string | null }>;
}

export interface CalendarPeriod {
  year: number;
  period_number: number;
  start_date: string;
  end_date: string;
  /** Betaaldag van déze periode */
  payday: string | null;
  /** Betaaldag die binnen deze periode valt (= uitbetaling van de vorige periode) */
  payday_in_period: string | null;
}

type PortalTab<T> = { rows: T; fetched_at: string } | undefined;
export interface AfasPortal {
  taken?: PortalTab<Array<{ id: string | null; datum: string | null; onderwerp: string; actie: string | null; soort: string | null }>>;
  ingestuurd?: PortalTab<Array<{ id: string | null; datum: string | null; onderwerp: string; soort: string | null; status: string | null }>>;
  dossier?: PortalTab<Array<{ id: string | null; datum: string | null; titel: string; soort: string | null; door: string | null }>>;
  verzuim?: PortalTab<Array<{ van: string | null; tot: string | null; soort: string | null; pct: number | null }>>;
  verzuim_huidig?: PortalTab<Array<{ van: string | null; tot: string | null; soort: string | null; pct: number | null }>>;
  persoonsdocumenten?: PortalTab<Array<{ soort: string | null; uitgifte: string | null; geldig_tot: string | null; land: string | null }>>;
  vog?: PortalTab<Array<Record<string, string>>>;
  belastingdienst?: PortalTab<Array<{ van: string | null; tot: string | null; loonheffingskorting: string | null; tabel: string | null }>>;
  opleidingen?: PortalTab<Array<Record<string, string>>>;
  cursussen?: PortalTab<Array<Record<string, string>>>;
  competenties?: PortalTab<Array<Record<string, string>>>;
  kwalificaties?: PortalTab<Array<{ naam: string; behaald: string | null; geldig_tot: string | null }>>;
  persoonlijk?: PortalTab<{ bijgewerkt: string[] }>;
  [kind: string]: PortalTab<unknown> | undefined;
}

export interface RegelVergelijking {
  sleutel: string;
  onderwerp: string;
  label: string;
  document: string;
  app: string;
  gelijk: boolean;
  document_id: number;
  titel: string;
}

export interface RegelsOverzicht {
  nu: {
    bron_datum: string | null;
    uurloon: { waarde: number; sinds: string; schaal: number | null; trede: number | null; app: number | null; klopt: boolean };
    contract: { soort: string | null; sinds: string; tot: string | null; app: string | null; klopt: boolean } | null;
    uren: { per_week: number | null; per_periode: number | null; parttime_pct: number | null; sinds: string; app: number | null; klopt: boolean } | null;
    functie: { naam: string | null; afdeling: string | null; sinds: string } | null;
  } | null;
  loongeschiedenis: Array<{ van: string; uurloon: number; schaal: number | null; trede: number | null; functie: string | null; reden: string | null; app_uurloon: number | null; klopt: boolean }>;
  cao: { document_id: number; titel: string } | null;
  tegenstrijdig: RegelVergelijking[];
  onderwerpen: Array<{
    key: string;
    label: string;
    vergelijkingen: RegelVergelijking[];
    feiten: Array<{ label: string; waarde: string; document_id: number; titel: string; datum: string | null; geldend: boolean }>;
  }>;
  certificaten: Array<{
    id: number;
    type: string;
    naam: string;
    uitgever: string | null;
    behaald_op: string | null;
    geldig_tot: string | null;
    source_document_id: number | null;
    status: 'geldig' | 'verloopt' | 'verlopen' | 'vervangen' | 'blijvend';
  }>;
  documenten: number;
}
